import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import api from "@/lib/api";
import { ArrowRight, BookOpen, MessageSquare } from "lucide-react";

const STAGE_META = {
  methodology: { title: "The Methodology", sub: "Think · Say · Do", accent: "var(--brand-primary)" },
  market:      { title: "The Market",      sub: "Client · Competitor · Opportunity", accent: "#0B3B36" },
  product:     { title: "The Product",     sub: "Product · Promise · Proof",         accent: "var(--brand-primary)" },
  admin:       { title: "The Admin",       sub: "Process · Platform · Precision",    accent: "var(--brand-primary)" },
  metrics:     { title: "The Metrics",     sub: "Measure · Monitor · Improve",       accent: "#8A5A22" },
  performance: { title: "The Lab", sub: "Review · Refine · Repeat",      accent: "#3E3A5C" },
};

export default function StageView() {
  const { stage } = useParams();
  const meta = STAGE_META[stage] || { title: stage, sub: "" };
  const [modules, setModules] = useState([]);
  const [scenarios, setScenarios] = useState([]);

  useEffect(() => {
    api.get("/modules", { params: { stage } }).then((r) => setModules(r.data));
    api.get("/scenarios", { params: { stage } }).then((r) => setScenarios(r.data));
  }, [stage]);

  if (stage === "performance") return <Navigate to="/call-review" replace />;

  return (
    <div className="space-y-14">
      <div>
        <Link to="/dashboard" className="label-eyebrow" data-testid="back-dashboard">← Dashboard</Link>
        <h1 className="font-display text-5xl md:text-6xl mt-4 leading-[1.02]" style={{color: meta.accent}}>{meta.title}</h1>
        <p className="mt-4 text-neutral-500 max-w-2xl text-lg">{meta.sub}</p>
      </div>

      {stage === "product" && (
        <Link to="/pressure-scorer" data-testid="pressure-scorer-card"
              className="block border-2 border-[color:var(--brand-primary)] bg-[color:var(--brand-primary)] text-white p-8 group">
          <div className="flex items-center justify-between">
            <span className="label-eyebrow text-white/60">Live product demo</span>
            <span className="label-eyebrow text-white/60">Sample data · Interactive</span>
          </div>
          <div className="mt-5 font-display text-2xl md:text-3xl leading-tight">Try the Pressure Scorer</div>
          <p className="mt-2 text-sm text-white/70 max-w-2xl">Hold the tool your prospects hold. Search suburbs, read vendor pressure scores and practise finding the intelligence drop for your Stage 2 walkthrough.</p>
          <span className="mt-5 inline-flex items-center gap-1 text-sm link-underline">Open the demo <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" /></span>
        </Link>
      )}

      <section>
        <div className="label-eyebrow mb-5">Modules</div>
        <div className="grid md:grid-cols-2 gap-6">
          {modules.map((m) => (
            <Link key={m.id} to={`/module/${m.id}`} data-testid={`module-card-${m.id}`} className="card-hairline p-7 flex flex-col">
              <div className="flex items-center justify-between">
                <BookOpen size={18} strokeWidth={1.25} className="text-[var(--brand-primary)]" />
                <div className="label-eyebrow">{(m.lessons||[]).length} lessons</div>
              </div>
              <div className="mt-8 font-display text-2xl leading-tight">{m.title}</div>
              <div className="mt-2 text-sm text-neutral-500 flex-1">{m.summary}</div>
              <div className="mt-6 text-sm text-[var(--brand-primary)] flex items-center gap-1 link-underline">Open module <ArrowRight size={14}/></div>
            </Link>
          ))}
          {modules.length === 0 && <div className="text-sm text-neutral-500">No modules yet for this stage.</div>}
        </div>
      </section>

      {scenarios.length > 0 && (
        <section>
          <div className="label-eyebrow mb-5">Live Role-Plays for this stage</div>
          <div className="grid md:grid-cols-3 gap-6">
            {scenarios.map((s) => (
              <Link
                key={s.id}
                to={s.kind === "ai_chat" ? `/roleplay/${s.id}` : `/branch/${s.id}`}
                data-testid={`scenario-card-${s.id}`}
                className="card-hairline p-6 flex flex-col"
              >
                <div className="flex items-center justify-between">
                  <MessageSquare size={16} strokeWidth={1.25} className="text-[var(--brand-primary)]" />
                  <span className="label-eyebrow">{s.kind === "ai_chat" ? "AI CHAT" : "BRANCHING"}</span>
                </div>
                <div className="mt-6 font-display text-xl leading-tight">{s.title}</div>
                <div className="mt-2 text-sm text-neutral-500 flex-1">{s.description}</div>
                <div className="mt-4 label-eyebrow text-[var(--brand-primary)]">{s.difficulty}</div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
